import { GalleryViewMedia } from '../../types/galleryTypes';
import { PhotoView } from '../../../../../types';
import { UseGalleryViewArgs } from './GalleryView.typed';
import getConfig from 'next/config';
import { useState } from 'react';

const useGalleryView = ({
  videoConfig,
  youTubeVideoId,
  youTubeReviewVideoId,
  link360,
  link360Interior,
  photos,
  isYoutubeReviewOpen,
}: UseGalleryViewArgs) => {
  const {
    publicRuntimeConfig: { CDN_STATIC_ASSETS },
  } = getConfig();
  const NO_IMAGE = `${CDN_STATIC_ASSETS}/images/fallbacks/no-image-gray.svg`;
  const THUMBNAIL_360 = `${CDN_STATIC_ASSETS}/images/fallbacks/thumbnail-360.webp`;
  const [idMediaSelected, setMediaSelected] = useState('');
  const isCitNowMedia =
    videoConfig?.videoProvider === 'CITNOW' && videoConfig?.streamingUrl;

  const getGalleryViewMedia = () => {
    const galleryViewMedia: GalleryViewMedia[] = [];
    const mediaLink360 = link360 || link360Interior;
    const mediaYouTubeVideo =
      youTubeVideoId || youTubeReviewVideoId || isCitNowMedia;

    if (isCitNowMedia) {
      galleryViewMedia.push({
        id: 'citnow_video',
        src: THUMBNAIL_360,
        isVideo: true,
        is360: false,
        isPhoto: false,
        isIframe: true,
        hasReview: false,
        width: 640,
        height: 360,
        viewed: false,
      });
    } else if (youTubeVideoId) {
      galleryViewMedia.push({
        id: youTubeVideoId,
        src: videoConfig?.customProperties?.thumbnail_url_0 || NO_IMAGE,
        isVideo: true,
        is360: false,
        isPhoto: false,
        isIframe: false,
        hasReview: false,
        viewed: false,
      });
    }

    if (mediaLink360) {
      galleryViewMedia.push({
        id: mediaLink360,
        src: THUMBNAIL_360,
        isVideo: false,
        is360: true,
        isPhoto: false,
        isIframe: false,
        hasReview: false,
        viewed: false,
      });
    }

    if (photos?.length) {
      photos.forEach((photo: PhotoView, index) => {
        galleryViewMedia.push({
          id: `photo_${index}`,
          src: photo.xLargeWebp,
          isVideo: false,
          is360: false,
          isPhoto: true,
          isIframe: false,
          hasReview: false,
          alt: photo.name || '',
        });
      });
      if (galleryViewMedia?.length > 1 && galleryViewMedia.length < 4) {
        Array.from({ length: 4 - galleryViewMedia.length }).forEach(() => {
          galleryViewMedia.push({
            src: NO_IMAGE,
            isVideo: false,
            is360: false,
            isPhoto: true,
            isIframe: false,
            hasReview: false,
          });
        });
      }

      if (mediaYouTubeVideo || mediaLink360) {
        const photoIndex = galleryViewMedia.findIndex((item) => item.isPhoto);
        if (photoIndex !== -1) {
          const [photoObject] = galleryViewMedia.splice(photoIndex, 1);
          galleryViewMedia.unshift(photoObject);
        }
      }
    }

    // On open we want to set the first item to be viewed
    if (galleryViewMedia.length > 0) {
      galleryViewMedia[0].viewed = true;
      if (youTubeReviewVideoId) {
        galleryViewMedia[0].hasReview = true;
      }
    }
    return galleryViewMedia;
  };

  const getGalleryMedia = () => {
    const galleryViewMedia: GalleryViewMedia[] = [];

    if (isCitNowMedia) {
      galleryViewMedia.push({
        id: 'citnow_video',
        src: videoConfig.streamingUrl as string,
        isVideo: true,
        is360: false,
        isPhoto: false,
        isIframe: true,
        hasReview: false,
        width: 640,
        height: 360,
        viewed: false,
      });
    }

    if (youTubeVideoId) {
      galleryViewMedia.push({
        id: youTubeVideoId,
        src: videoConfig?.customProperties?.thumbnail_url_0 || NO_IMAGE,
        isVideo: true,
        is360: false,
        isPhoto: false,
        isIframe: false,
        hasReview: false,
        viewed: false,
      });
    }

    if (link360) {
      galleryViewMedia.push({
        id: link360,
        src: THUMBNAIL_360,
        isVideo: false,
        is360: true,
        isPhoto: false,
        isIframe: false,
        hasReview: false,
        viewed: false,
        extendClass: '360-imp',
      });
    }

    if (link360Interior) {
      galleryViewMedia.push({
        id: link360Interior,
        src: THUMBNAIL_360,
        isVideo: false,
        is360: true,
        isPhoto: false,
        isIframe: false,
        hasReview: false,
        viewed: false,
        extendClass: '360-interior-imp',
      });
    }

    if (photos?.length) {
      photos.forEach((photo: PhotoView, index) => {
        galleryViewMedia.push({
          id: `photo_${index}`,
          src: photo.xLargeWebp,
          isVideo: false,
          is360: false,
          isPhoto: true,
          isIframe: false,
          hasReview: false,
          alt: photo.name || '',
        });
      });
    }

    if (youTubeReviewVideoId) {
      galleryViewMedia.push({
        id: youTubeReviewVideoId,
        src: `https://img.youtube.com/vi/${youTubeReviewVideoId}/maxresdefault.jpg`,
        isVideo: true,
        is360: false,
        isPhoto: false,
        isIframe: false,
        hasReview: true,
        viewed: false,
        extendClass: 'review-imp',
      });
    }

    // On open we want to set the first item to be viewed
    if (galleryViewMedia.length > 0) {
      galleryViewMedia[0].viewed = true;
    }

    return galleryViewMedia;
  };

  const media = getGalleryMedia();
  const mediaPreview = getGalleryViewMedia();

  const handleOnMediaClick = (id?: string) => {
    id && setMediaSelected(id);
  };

  const indexGallery = media.find((item) => item.id === idMediaSelected);
  const mediaSelected = indexGallery && media.indexOf(indexGallery);

  const getIndexGallery = () => {
    if (isYoutubeReviewOpen) {
      const indexYoutubeReviewMedia = media.find((item) => item.hasReview);
      return indexYoutubeReviewMedia && media.indexOf(indexYoutubeReviewMedia);
    } else if (mediaSelected) {
      return mediaSelected;
    }
    return 0;
  };

  const [mainGalleryMedia, ...galleryMedia] = mediaPreview;
  let mainGalleryMediaImage = mainGalleryMedia
    ? mainGalleryMedia.src
    : `${CDN_STATIC_ASSETS}/images/fallbacks/no-image-ad.svg`;
  const hasCarousel = mediaPreview && mediaPreview.length > 1;
  const photoCount =
    mediaPreview && mediaPreview.length > 4 ? mediaPreview.length - 4 : null;

  return {
    media,
    mediaPreview,
    photoCount,
    THUMBNAIL_360,
    mainGalleryMedia,
    galleryMedia,
    mainGalleryMediaImage,
    hasCarousel,
    handleOnMediaClick,
    getIndexGallery,
  };
};

export { useGalleryView };
